import { FlaskConical } from 'lucide-react';
import { UNGATED_PREVIEW_SHA } from '@/buildConstants';
import { BOTTOM_BAR_ROW, ViewportBottomBar } from '@/components/AppViewport';
import { isDrawerPanelView } from '@/shell/drawerPanel';
import { UNGATED_PREVIEW_BAR_CLASS } from '@/components/ungatedPreviewBarClass';
import { banners } from '@featit/shared/i18n/messages/en/banners';
import { registerEnglishArea } from '@/i18n/catalogue';
import { useT } from '@/i18n/LocaleProvider';

registerEnglishArea('banners', banners);

/**
 * F-0309 DP-4: dev served from a commit the gate has not passed says so, with the commit, above
 * everything else. `scripts/dev-preview.sh` builds dev that way so a batch can be looked at while its
 * gate runs; a screenshot or a report taken from it then names what it shows. Every other build
 * renders nothing.
 *
 * A top-bar row inside `AppViewport` (I-0580): it takes its height out of the window, and never
 * covers the page.
 */
export function UngatedPreviewBar({ sha = UNGATED_PREVIEW_SHA }: { sha?: string }) {
  const t = useT();
  const commit = sha.trim().slice(0, 12);
  // I-1138: not in the More drawer's panel view; the screen behind it already says it.
  if (!commit || isDrawerPanelView()) return null;
  return (
    <ViewportBottomBar order="preview">
      <div
        role="note"
        aria-label={t('banners.ungatedPreview.label', { commit })}
        data-bottom-bar="ungated-preview"
        className={`flex ${BOTTOM_BAR_ROW} ${UNGATED_PREVIEW_BAR_CLASS}`}
      >
        <FlaskConical className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        {/*
        I-2399: the title and its commit keep one line; at 390 they wrapped to two and the note was
        cut to "Not yet passed by the gate. De…". A phone reads the short note, whose words are the
        half that is not in the title already. I-2556: the notes give up their room first, and the
        title is cut short only when it alone is wider than the view; kept whole at the largest text
        size (572 px), it widened a phone's page to 639 px and every dialog drawn over it.
      */}
        <span data-preview-title="" className="min-w-0 truncate font-semibold">
          {t('banners.ungatedPreview.title', { commit })}
        </span>
        <span className="min-w-0 shrink-[100] truncate text-violet-800 max-sm:hidden dark:text-violet-200">
          {t('banners.ungatedPreview.note')}
        </span>
        <span
          data-preview-note-short=""
          className="min-w-0 shrink-[100] truncate text-violet-800 sm:hidden dark:text-violet-200"
        >
          {t('banners.ungatedPreview.noteShort')}
        </span>
      </div>
    </ViewportBottomBar>
  );
}
